import React, { useEffect, useRef, useState } from 'react';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { useFindTaskTypeEntities } from '../TaskType.hooks';

export interface TaskTypeEntitySelectorProps {
  value?: string;
  disabled?: boolean;
  errorMessage?: string;
  onSelected?: (entity: string) => void;
  onChange?: (entity: string | null) => void;
}

const TaskTypeEntitySelector: React.FC<TaskTypeEntitySelectorProps> = (
  props: TaskTypeEntitySelectorProps
) => {
  const {
    value: entityName,
    disabled,
    errorMessage,
    onChange,
    onSelected
  } = props;
  const previousEntityRef = useRef<string | undefined>(entityName);
  const [selected, setSelected] = useState<string | undefined>();
  const [entities, setEntities] = useState<string[]>([]);
  const [{ data, isLoading, error }, findEntities] = useFindTaskTypeEntities();

  useEffect(() => {
    findEntities();
  }, [findEntities]);

  useEffect(() => {
    data && setEntities(data);
  }, [data]);

  // if entityName changes, reselect entity
  useEffect(() => {
    if (previousEntityRef.current !== entityName) {
      setSelected(undefined);
      previousEntityRef.current = entityName;
    }
  }, [entityName]);

  useEffect(() => {
    // if no selected entity yet
    if (selected) {
      return;
    }
    const selectedEntity: string | undefined = entities.find(
      (entity: string) => entity === entityName
    );
    if (selectedEntity) {
      setSelected(selectedEntity);
      onSelected?.(selectedEntity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, entityName]);

  return (
    <Autocomplete
      loading={isLoading}
      disabled={disabled}
      value={selected || null}
      options={entities}
      getOptionSelected={(option: string) => option === entityName}
      onChange={(_event: React.ChangeEvent<{}>, value: string | null) => {
        if (value) {
          previousEntityRef.current = value;
        }
        setSelected(value || undefined);
        onChange?.(value);
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Select Target Entity'
          placeholder='Choose a Target Entity'
          error={!!(error?.message || errorMessage)}
          helperText={error?.message || errorMessage}
        />
      )}
    />
  );
};

export default TaskTypeEntitySelector;
